/**
 * Utility functions for derived usage metrics.
 */

/**
 * Calculate cost per token.
 * @param cost - Total cost in USD
 * @param tokens - Total number of tokens
 * @returns Cost per token, or null if inputs are invalid
 */
export function getCostPerToken(cost: number, tokens: number): number | null {
  if (
    !Number.isFinite(cost) ||
    !Number.isFinite(tokens) ||
    tokens <= 0 ||
    cost < 0
  ) {
    return null;
  }
  return cost / tokens;
}

/**
 * Format cost per token for display.
 * @param cost - Total cost in USD
 * @param tokens - Total number of tokens
 * @returns Formatted string like "$0.0001/tok"
 */
export function formatCostPerToken(cost: number, tokens: number): string {
  const perToken = getCostPerToken(cost, tokens);
  if (perToken === null) {
    return "—";
  }

  // Use scientific notation for very small values
  if (perToken < 0.000_001) {
    return `$${perToken.toExponential(2)}/tok`;
  }

  // Use appropriate precision based on magnitude
  if (perToken < 0.0001) {
    return `$${perToken.toFixed(7)}/tok`;
  }
  if (perToken < 0.01) {
    return `$${perToken.toFixed(5)}/tok`;
  }
  return `$${perToken.toFixed(4)}/tok`;
}

/**
 * Calculate cache hit rate as a percentage.
 * @param cachedInput - Number of cached input tokens
 * @param totalInput - Total number of input tokens
 * @returns Cache hit rate (0-100), or null if inputs are invalid
 */
export function getCacheHitRate(
  cachedInput: number,
  totalInput: number,
): number | null {
  if (
    !Number.isFinite(cachedInput) ||
    !Number.isFinite(totalInput) ||
    totalInput <= 0 ||
    cachedInput < 0
  ) {
    return null;
  }
  return (cachedInput / totalInput) * 100;
}

/**
 * Format cache hit rate for display.
 * @param rate - Cache hit rate (0-100)
 * @returns Formatted string like "45.2%"
 */
export function formatCacheHitRate(rate: number | null): string {
  if (rate === null || !Number.isFinite(rate)) {
    return "—";
  }
  return `${rate.toFixed(1)}%`;
}

/**
 * Calculate input/output token ratio.
 * @param input - Number of input tokens
 * @param output - Number of output tokens
 * @returns Object with normalized percentages
 */
export function getInputOutputRatio(
  input: number,
  output: number,
): { input: number; output: number } | null {
  if (
    !Number.isFinite(input) ||
    !Number.isFinite(output) ||
    input < 0 ||
    output < 0
  ) {
    return null;
  }

  const total = input + output;
  if (total === 0) {
    return { input: 0, output: 0 };
  }

  return { input: (input / total) * 100, output: (output / total) * 100 };
}

/**
 * Calculate percentage change between two values.
 * @param current - Current value
 * @param previous - Previous value
 * @returns Percentage change, or null if inputs are invalid
 */
export function getPercentageChange(
  current: number,
  previous: number,
): number | null {
  if (
    !Number.isFinite(current) ||
    !Number.isFinite(previous) ||
    previous === 0
  ) {
    return null;
  }
  return ((current - previous) / Math.abs(previous)) * 100;
}

/**
 * Format a ratio as a percentage string.
 * @param numerator - The numerator value
 * @param denominator - The denominator value
 * @returns Formatted string like "45.2%", or null if inputs are invalid
 */
export function formatPercentage(
  numerator: number | null,
  denominator: number | null,
): null | string {
  if (numerator === null || denominator === null || denominator <= 0) {
    return null;
  }
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

/**
 * Format percentage change with sign.
 * @param change - Percentage change
 * @returns Formatted string like "+12.5%" or "-8.3%"
 */
export function formatPercentageChange(change: number | null): string {
  if (change === null || !Number.isFinite(change)) {
    return "—";
  }
  const sign = change >= 0 ? "+" : "";
  return `${sign}${change.toFixed(1)}%`;
}
